import type { ChangeEvent, JSX } from "react";

/**
 * Die Kontotypen, wie `renderUsers` sie IMMER in dieser Reihenfolge anbietet (sowohl im
 * Anlegen-Formular, index.html:6263, als auch je Tabellenzeile, index.html:6271-6273) - Global
 * Constraint 8: keine frei konfigurierbaren Rollen.
 *
 * BEFUND am Task-Brief (Schritt 2, "bietet genau die drei Rollen an"): der Brief erwartet die
 * Reihenfolge `["admin", "product_manager", "payment_manager"]`. Der Bestand - selbst an der
 * eingecheckten Baseline nachgezaehlt (Verzeichnis `test/visual/baseline`, je Projekt die Datei
 * `users-standard.signatur.json`, Knoten 1297-1299 bzw. 1321-1323: "Standard", "Zahlungsansicht",
 * "Administrator") - fuehrt sie tatsaechlich `["product_manager", "payment_manager", "admin"]`.
 * Portiert ist die woertliche Bestandsreihenfolge; siehe Task-Bericht.
 *
 * `read_only` ("Nur lesen") steht als VIERTER und letzter Eintrag dahinter: neu am 30.10.2026 (Plan
 * "Kontotyp Nur lesen"), ohne Vorbild in `index.html` - die drei Bestandseintraege behalten Platz und
 * Wortlaut. Das Konto sieht dasselbe wie ein Standard-Konto, aendert nichts und braucht keinen JTL-Login.
 */
export const ROLLEN = ["product_manager", "payment_manager", "admin", "read_only"] as const;
export type Rolle = (typeof ROLLEN)[number];

/**
 * `normalizedUserRole` (index.html:5288-5292): jede unbekannte/fehlende Rolle faellt auf
 * `product_manager` zurueck - nicht nur die Anzeige, auch die tatsaechlich fuer `selected`
 * herangezogene Rolle. AUSNAHME `read_only`: sie bleibt `read_only`. Fiele sie auf `product_manager`
 * zurueck, zeigte die Oberflaeche ein Nur-Leser-Konto als "Standard" (Schreibrechte, die der Server
 * mit 403 verweigert), und ein Rollenwechsel in der Zeile bote es als bereits gewaehlte Standardrolle an.
 */
export function normalisierteRolle(rolle: unknown): Rolle {
  if (rolle === "admin") return "admin";
  if (rolle === "payment_manager") return "payment_manager";
  if (rolle === "read_only") return "read_only";
  return "product_manager";
}

/**
 * `userRoleLabel` (index.html:5294-5299). Die einzige Ablage dieser Zuordnung: `components/App/MarkenMenue.tsx`
 * (Konto-Panel, Liste "Zuletzt aktiv") und `AktivitaetenPage/aktivitaetenAnzeige.ts` importieren sie von hier.
 * `state/berechtigungen.ts` fuehrt bewusst nur Seiten-/Rechte-Zustand (siehe dortiger Kopfkommentar), keine
 * Anzeigetexte. Ohne den Zweig fuer `read_only` erschiene ein Nur-Leser-Konto ueberall als "Standard".
 */
export function rollenBezeichnung(rolle: unknown): string {
  const normal = normalisierteRolle(rolle);
  if (normal === "admin") return "Administrator";
  if (normal === "payment_manager") return "Zahlungsansicht";
  if (normal === "read_only") return "Nur lesen";
  return "Standard";
}

export interface RollenAuswahlProps {
  /** `id="newUserRole"` im Anlegen-Formular (index.html:6263) - in Tabellenzeilen ungesetzt. */
  id?: string;
  /**
   * Gesetzt => kontrollierte Auswahl (Tabellenzeile, `onchange="updateUser(...)"`,
   * index.html:6270-6274). Ungesetzt => unkontrolliert mit `defaultValue` (Anlegen-Formular, das
   * den Wert erst beim Absenden liest, index.html:6294).
   */
  value?: string;
  defaultValue?: string;
  /**
   * Meldet die gewaehlte Rolle - in BEIDEN Betriebsarten. Im Anlegen-Formular (unkontrolliert) nutzt
   * `NutzerFormular.tsx` es, um den JTL-Login nur bei einem schreibenden Kontotyp zur Pflicht zu machen;
   * der Wert selbst wird dort weiterhin erst beim Absenden gelesen.
   */
  onChange?: (rolle: string) => void;
  /** Eigenes Konto: die Zeile darf ihre eigene Rolle nicht aendern (index.html:6270). */
  disabled?: boolean;
  /** `aria-label="Kontotyp"` je Tabellenzeile (index.html:6270) - im Formular uebernimmt das die umschliessende `<label>`. */
  ariaLabel?: string;
}

/**
 * `<select>` mit den vier Kontotyp-Optionen - weder im Anlegen-Formular noch je Tabellenzeile
 * traegt der Bestand eine eigene Klasse (index.html:6263, 6270-6274: nur `id`/`aria-label`), daher
 * hier bewusst OHNE `className`-Prop.
 */
export function RollenAuswahl({
  id,
  value,
  defaultValue,
  onChange,
  disabled,
  ariaLabel,
}: RollenAuswahlProps): JSX.Element {
  function geaendert(event: ChangeEvent<HTMLSelectElement>): void {
    onChange?.(event.target.value);
  }
  const wertProps =
    value !== undefined
      ? { value: normalisierteRolle(value) }
      : { defaultValue: normalisierteRolle(defaultValue ?? "product_manager") };
  return (
    <select id={id} aria-label={ariaLabel} disabled={disabled} onChange={geaendert} {...wertProps}>
      <option value="product_manager">Standard</option>
      <option value="payment_manager">Zahlungsansicht</option>
      <option value="admin">Administrator</option>
      <option value="read_only">Nur lesen</option>
    </select>
  );
}
